import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import {
  accent8 as accent, useSlide8, Seg,
} from "./slide8.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 8 in ALL mode — flowing, scrollable, no canvas.
   (The Practice-mode body version keeps the interactive breathing guide instead.) */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.7, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide8Mobile: React.FC = () => {
  const { meta: slide8Meta, steps: slide8Steps, why: slide8Why, duration: slide8Duration } = useSlide8();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#a06a3a", textTransform: "uppercase", marginBottom: 8 }}>
        {slide8Meta.breadcrumbPre} · <span style={{ color: accent }}>{slide8Meta.breadcrumbAccent}</span>
      </div>

      {/* sanskrit + title */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 3, color: "rgba(224,123,57,0.55)", marginBottom: 6 }}>{slide8Meta.sanskrit}</div>
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 25, lineHeight: 1.2, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide8Meta.titlePre}<span style={{ color: accent }}>{slide8Meta.titleAccentWord}</span>{slide8Meta.titlePost}
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#a5896f", margin: "0 0 20px", lineHeight: 1.5 }}>
        {slide8Meta.subtitle}
      </p>

      {/* why first */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide8Meta.whyLabel}</div>
        <p style={txt}>{renderSegs(slide8Why, "#f8c898")}</p>
      </div>

      {/* how to practise */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide8Meta.howLabel}</div>
        {slide8Steps.map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: accent, flexShrink: 0, marginTop: 7, opacity: 0.75 }} />
            <div>
              <span style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 14.5, color: "#f8d0b0" }}>{s.step} </span>
              <span style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14.5, lineHeight: 1.55, color: "#9bbdd6" }}>{s.desc}</span>
            </div>
          </div>
        ))}
      </div>

      {/* duration callout */}
      <div style={{ background: "rgba(224,123,57,0.06)", border: "1px solid rgba(224,123,57,0.22)", borderRadius: 10, padding: "14px 16px" }}>
        <div style={lbl}>{slide8Meta.durationLabel}</div>
        <p style={{ ...txt, color: "#f8c898" }}>{renderSegs(slide8Duration, "#ffe0c0")}</p>
      </div>
    </div>
  );
};

export default HTLDSlide8Mobile;
